import React, { useState, useEffect } from 'react';
import { X, HelpingHand as PrayingHands } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface PrayerRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PrayerRequestModal({ isOpen, onClose }: PrayerRequestModalProps) {
  const [name, setName] = useState('');
  const [request, setRequest] = useState('');
  const [requestType, setRequestType] = useState<'public' | 'anonymous' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [session, setSession] = useState<any>(null);

  useEffect(() => {
    // Verificar se o usuário está logado
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      // Se o usuário estiver logado, preencher o nome com o nome do perfil
      if (session?.user?.user_metadata?.name) {
        setName(session.user.user_metadata.name);
      }
    });
  }, [isOpen]); // Atualizar quando o modal for aberto

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      if (!session) {
        throw new Error('Você precisa estar logado para enviar um pedido de oração');
      }

      const prayerRequest = {
        name: requestType === 'anonymous' ? 'Anônimo' : name,
        request,
        is_public: requestType === 'public',
        is_anonymous: requestType === 'anonymous',
        user_id: session.user.id
      };

      const { error: submitError } = await supabase
        .from('prayer_requests')
        .insert([prayerRequest]);

      if (submitError) throw submitError;

      setRequest('');
      setRequestType(null);
      onClose();
      
      alert('Pedido de oração enviado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao enviar pedido:', error);
      setError(error.message || 'Erro ao enviar o pedido. Por favor, tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 relative shadow-elevation-3">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <X className="h-6 w-6 text-gray-500" />
        </button>
        
        <div className="text-center mb-6">
          <div className="bg-brand-light w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <PrayingHands className="h-8 w-8 text-brand-teal" />
          </div>
          <h2 className="text-2xl font-bold text-brand-teal">Pedido de Oração</h2>
          <p className="text-gray-600 mt-2">
            Compartilhe seu pedido conosco. Nossa equipe de intercessão orará por você.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 text-green-600 rounded-lg text-sm">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Campo de nome - mostrado apenas se não for anônimo */}
          {requestType !== 'anonymous' && (
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Seu Nome
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-shadow"
                placeholder="Digite seu nome"
                required={requestType !== 'anonymous'}
              />
            </div>
          )}

          <div>
            <label htmlFor="request" className="block text-sm font-medium text-gray-700 mb-1">
              Seu Pedido de Oração
            </label>
            <textarea
              id="request"
              value={request}
              onChange={(e) => setRequest(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-teal focus:border-brand-teal h-32 transition-shadow"
              placeholder="Compartilhe seu pedido de oração..."
              required
            />
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">Como você deseja fazer seu pedido?</p>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setRequestType('anonymous')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  requestType === 'anonymous'
                    ? 'border-brand-teal bg-brand-light text-brand-teal'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium mb-1">Anônimo</div>
                <div className="text-sm text-gray-600">
                  Seu nome não será exibido
                </div>
              </button>

              <button
                type="button"
                onClick={() => setRequestType('public')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  requestType === 'public'
                    ? 'border-brand-teal bg-brand-light text-brand-teal'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium mb-1">Público</div>
                <div className="text-sm text-gray-600">
                  Compartilhado com a igreja
                </div>
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || !requestType}
            className="w-full bg-brand-teal text-white py-3 px-4 rounded-xl hover:bg-brand-teal/90 transition-colors disabled:opacity-50 shadow-elevation-1 hover:shadow-elevation-2 active:shadow-elevation-1"
          >
            {submitting ? 'Enviando...' : 'Enviar Pedido'}
          </button>
        </form>
      </div>
    </div>
  );
}